// @ts-strict-ignore
import { useQuery } from "@apollo/client";
import {
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Radio,
  Typography,
} from "@material-ui/core";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import CloseIcon from "@material-ui/icons/Close";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import { Box, Button, Text } from "@saleor/macaw-ui-next";
import React from "react";

import { CMS_DELIVERY_DATE_PREVIEW_QUERY } from "../cmsQueries";
import { CMSDeliverySlot } from "../types";

interface SlotPreviewDialogProps {
  open: boolean;
  slot: CMSDeliverySlot | null;
  onClose: () => void;
}

export const SlotPreviewDialog: React.FC<SlotPreviewDialogProps> = ({
  open,
  slot,
  onClose,
}) => {
  const { data: dateData, loading: dateLoading } = useQuery(CMS_DELIVERY_DATE_PREVIEW_QUERY, {
    variables: { slotKey: slot?.slotKey || "" },
    skip: !slot?.slotKey,
    fetchPolicy: "network-only",
  });

  if (!slot) return null;

  const simulatedDateStr = dateData?.cmsDeliveryDatePreview;
  const formattedSimulatedDate = simulatedDateStr
    ? new Date(simulatedDateStr).toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            Delivery Slot Storefront Preview
          </Typography>
          <IconButton size="small" onClick={onClose} title="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={3}>
          <Typography variant="caption" color="textSecondary">
            This is how your customers see and select this delivery option during checkout in the mobile app and website:
          </Typography>

          {/* Customer Storefront Checkout Card Mockup */}
          <Box
            padding={3}
            style={{
              borderRadius: 12,
              border: "2px solid #16a34a",
              backgroundColor: "#f0fdf4",
              display: "flex",
              alignItems: "flex-start",
              gap: 12,
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05)",
            }}
          >
            <Radio checked color="primary" style={{ padding: 0, marginTop: 2, color: "#16a34a" }} />
            <Box display="flex" flexDirection="column" gap={1} width="100%">
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1" style={{ fontWeight: 700, color: "#14532d" }}>
                  {slot.label}
                </Typography>
                <Chip
                  size="small"
                  label={slot.isActive ? "Available" : "Disabled"}
                  style={{
                    backgroundColor: slot.isActive ? "#dcfce7" : "#fee2e2",
                    color: slot.isActive ? "#15803d" : "#b91c1c",
                    fontWeight: 600,
                  }}
                />
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                <AccessTimeIcon style={{ fontSize: 16, color: "#15803d" }} />
                <Typography variant="body2" style={{ color: "#166534", fontWeight: 600 }}>
                  Time Window: {slot.timeWindow}
                </Typography>
              </Box>

              {/* Dynamic Simulated Delivery Date */}
              <Box
                marginTop={1}
                padding={2}
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: 8,
                  border: "1px solid #bbf7d0",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <EventAvailableIcon style={{ fontSize: 22, color: "#16a34a" }} />
                <Box display="flex" flexDirection="column">
                  <Typography variant="caption" color="textSecondary">
                    Calculated Delivery Date for Order Placed Now:
                  </Typography>
                  {dateLoading ? (
                    <Box display="flex" alignItems="center" gap={1} marginTop={0.5}>
                      <CircularProgress size={14} />
                      <Typography variant="caption">Calculating delivery cycle...</Typography>
                    </Box>
                  ) : formattedSimulatedDate ? (
                    <Typography variant="body2" style={{ fontWeight: 700, color: "#065f46" }}>
                      {formattedSimulatedDate}
                    </Typography>
                  ) : (
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                      + {slot.dayOffset} day(s) from order date
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Slot Rules & Configuration Grid */}
          <Box
            padding={3}
            style={{
              backgroundColor: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              display: "flex",
              flexDirection: "column",
              gap: 16,
            }}
          >
            <Typography variant="subtitle2" style={{ fontWeight: 600, color: "#334155" }}>
              Slot Calculation Rules
            </Typography>

            <Box
              display="grid"
              style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}
              gap={2}
            >
              <Box display="flex" flexDirection="column" gap={0.5}>
                <Typography variant="caption" color="textSecondary">
                  Slot Key
                </Typography>
                <Typography variant="body2" style={{ fontFamily: "monospace", fontWeight: 700 }}>
                  {slot.slotKey}
                </Typography>
              </Box>

              <Box display="flex" flexDirection="column" gap={0.5}>
                <Typography variant="caption" color="textSecondary">
                  Day Offset (Lead Time)
                </Typography>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  +{slot.dayOffset} Day(s)
                </Typography>
              </Box>

              <Box display="flex" flexDirection="column" gap={0.5}>
                <Typography variant="caption" color="textSecondary">
                  Daily Order Cutoff
                </Typography>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  {slot.cutoffTime ? `${slot.cutoffTime} (Orders after this shift to next cycle)` : "No daily cutoff"}
                </Typography>
              </Box>

              <Box display="flex" flexDirection="column" gap={0.5}>
                <Typography variant="caption" color="textSecondary">
                  Special Rules
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {slot.useNextSat ? (
                    <Chip size="small" label="Saturday Harvest Only" style={{ backgroundColor: "#fef3c7", color: "#b45309" }} />
                  ) : (
                    <Typography variant="body2">Standard schedule</Typography>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Mapped Product Types */}
            <Box display="flex" flexDirection="column" gap={1} marginTop={1}>
              <Typography variant="caption" color="textSecondary">
                Eligible Product Types for this Slot:
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {slot.productTypes?.length ? (
                  slot.productTypes.map(p => (
                    <Chip key={p.id} size="small" label={p.name} />
                  ))
                ) : (
                  <Text size={1} color="default2">Available for all product types</Text>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions style={{ padding: "12px 24px" }}>
        <Button variant="secondary" onClick={onClose}>
          Close Preview
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SlotPreviewDialog;
